import { CourtHearingResponse } from "@/api";
import { dateOnlyKey, dateOnlyToLocalDate } from "@/common/utils/dateOnly";

/** Group key of the hearings whose proceeding has no court. */
const NO_COURT_KEY = "no-court";

export interface CourtInfo {
  courtId: string | null;
  courtName: string | null;
  courtShortCode: string | null;
}

export interface CourtGroup extends CourtInfo {
  key: string;
  rows: CourtHearingResponse[];
}

export interface DayGroup {
  key: string;
  date: Date;
  rows: CourtHearingResponse[];
}

export interface DayWithCourts {
  key: string;
  date: Date;
  courts: CourtGroup[];
}

export interface CourtWithDays extends CourtInfo {
  key: string;
  days: DayGroup[];
}

function courtKeyOf(row: CourtHearingResponse): string {
  return row.court?.id ?? NO_COURT_KEY;
}

function courtInfoOf(row: CourtHearingResponse): CourtInfo {
  return {
    courtId: row.court?.id ?? null,
    courtName: row.court?.name ?? null,
    courtShortCode: row.court?.shortCode ?? null,
  };
}

/** Courts by name; hearings without a court come last. */
function compareCourts(a: CourtInfo, b: CourtInfo): number {
  if (a.courtName === null) {
    return b.courtName === null ? 0 : 1;
  }
  if (b.courtName === null) {
    return -1;
  }
  return a.courtName.localeCompare(b.courtName, "de");
}

function compareRows(a: CourtHearingResponse, b: CourtHearingResponse): number {
  return (
    a.time.getTime() - b.time.getTime() ||
    a.client.lastName.localeCompare(b.client.lastName, "de") ||
    a.client.firstName.localeCompare(b.client.firstName, "de")
  );
}

/**
 * Groups rows by key, taking the group's descriptive fields from its first row, and sorts the rows
 * inside each group.
 */
function groupRows<TGroup extends { key: string; rows: CourtHearingResponse[] }>(
  rows: CourtHearingResponse[],
  keyOf: (row: CourtHearingResponse) => string,
  createGroup: (key: string, firstRow: CourtHearingResponse) => TGroup
): TGroup[] {
  const groups = new Map<string, TGroup>();
  for (const row of rows) {
    const key = keyOf(row);
    const group = groups.get(key);
    if (group) {
      group.rows.push(row);
    } else {
      groups.set(key, createGroup(key, row));
    }
  }
  return [...groups.values()].map((group) => ({ ...group, rows: [...group.rows].sort(compareRows) }));
}

function toCourtGroups(rows: CourtHearingResponse[]): CourtGroup[] {
  return groupRows(rows, courtKeyOf, (key, firstRow) => ({
    key,
    ...courtInfoOf(firstRow),
    rows: [firstRow],
  })).sort(compareCourts);
}

function toDayGroups(rows: CourtHearingResponse[]): DayGroup[] {
  return groupRows(
    rows,
    (row) => dateOnlyKey(row.date),
    (key, firstRow) => ({ key, date: dateOnlyToLocalDate(firstRow.date), rows: [firstRow] })
  ).sort((a, b) => a.key.localeCompare(b.key));
}

/** The by-date grouping ("nach Datum"): days ascending, within a day the courts by name. */
export function groupByDateThenCourt(rows: CourtHearingResponse[]): DayWithCourts[] {
  return toDayGroups(rows).map((day) => ({ key: day.key, date: day.date, courts: toCourtGroups(day.rows) }));
}

/** The by-court grouping ("nach Gericht"): courts by name, within a court the days ascending. */
export function groupByCourtThenDate(rows: CourtHearingResponse[]): CourtWithDays[] {
  return toCourtGroups(rows).map((court) => ({
    key: court.key,
    courtId: court.courtId,
    courtName: court.courtName,
    courtShortCode: court.courtShortCode,
    days: toDayGroups(court.rows),
  }));
}
